import { hashObject } from '@fullstory/util/hash';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, fullstorySelectors, MetricsRequestActions } from '@fullstory/redux';
import { useNotifications } from '@fullstory/react-hooks';
import {
  Color,
  SegmentSettings,
} from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { TimeInterval, TimeRange } from '@fullstory/proto/fs/api/segment';
import { UnnamedMetric } from '@fullstory/proto/fs/services/dx-objects';
import { generateRoutePath, METRIC_CREATE_WITH_HASH_ROUTE } from '@fullstory/routes';
import { useHistory } from 'react-router-dom';
import { createPath } from 'history';
import { int64 } from '@fullstory/proto/google/protobuf';
import { useCallback } from 'react';
import { BuiltInDimension, Measure, SortOrder, SortType } from '@fullstory/proto/fs/api/metrics';

const { getOrgId } = fullstorySelectors;

export const useRouteToMetric = () => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const history = useHistory();
  const notification = useNotifications();
  const createUnnamedMetric = useCallback(
    async (metric: UnnamedMetric) => {
      const hash = `${hashObject(metric)}`;
      await dispatch(MetricsRequestActions.createUnnamedMetric({ hash, metric, orgId }));
      return hash;
    },
    [dispatch, orgId],
  );

  const routeToMetric = useCallback(
    ({ timeRange, segments }: { segments: SegmentSettings[]; timeRange: TimeRange | null }) => {
      const newUnnamedMetric: UnnamedMetric = {
        name: 'Not defined Page by URL',
        description: '',
        metric: {
          single: {
            event: {
              primary: {
                visitedPage: {
                  equals: {
                    value: ['0' as int64],
                    spec: [],
                  },
                },
              },
              deps: [],
            },
            aggregation: {
              default: {},
            },
            rollingWindow: 0,
            rollingWindowType: Measure.RollingWindowType.ROLLING_WINDOW_TYPE_INVALID,
            rollingWindowInterval: TimeInterval.TIME_INTERVAL_INVALID,
          },
        },
        display: null,
        timeRange,
        timeInterval: TimeInterval.TIME_INTERVAL_ENTIRE_RANGE,
        segmentIds: [],
        segments,
        dimensionSettings: {
          sortOrder: SortOrder.SORT_ORDER_DESC,
          sortByDefault: false,
          sortType: SortType.SORT_TYPE_INVALID,
          limit: 50,
          computeTopLevel: true,
          computeNumGroups: false,
          allowAllPartialResults: false,
          noFallback: false,
          dimensions: [
            {
              builtInProperty: BuiltInDimension.DIMENSION_URL,
              prefix: '',
              limit: 0,
              computeAtLevel: false,
              eventRefinement: null,
            },
          ],
          dimension: null,
        },
        timeRangeComparison: null,
        singleNumberColor: Color.COLOR_INVALID,
      };

      createUnnamedMetric(newUnnamedMetric)
        .then(metricHash => {
          const pathname = generateRoutePath(METRIC_CREATE_WITH_HASH_ROUTE, {
            orgId,
            hash: metricHash,
          });
          history.push(createPath({ pathname, search: location.search }));
        })
        .catch(() => {
          notification?.error('Failed to view metric.');
        });
    },
    [createUnnamedMetric, history, notification, orgId],
  );

  return routeToMetric;
};
